import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { UserPen } from 'lucide-react';
import { z } from 'zod';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { PCMP_EDIT_LIMITS } from '../../constants/pcmpProposal.constants';
import { useEditPcmpProposalSection } from '../../hooks/useEditPcmpProposalSection';
import type { PcmpProposalDetail } from '../../types/pcmpProposal.types';
import {
  blankToNull,
  pcmpAmountSchema,
  pcmpEditReasonSchema,
  pcmpMobileSchema,
  pcmpOptionalTextSchema,
} from '../../utils/pcmpProposalEditForm';
import { PcmpEditDialog } from './PcmpEditDialog';
import { PcmpEditReasonField } from './PcmpEditReasonField';

const teacherFormSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, 'Name is required')
    .max(PCMP_EDIT_LIMITS.teacherName, `At most ${PCMP_EDIT_LIMITS.teacherName} characters`),
  mobile: pcmpMobileSchema('Mobile'),
  designation: pcmpOptionalTextSchema(PCMP_EDIT_LIMITS.shortText),
  subject: pcmpOptionalTextSchema(PCMP_EDIT_LIMITS.shortText),
  agreedAmount: pcmpAmountSchema('Agreed amount', { positive: false }),
  proposedAmount: pcmpAmountSchema('Proposed amount', { positive: true }),
  comments: pcmpOptionalTextSchema(PCMP_EDIT_LIMITS.longText),
  reason: pcmpEditReasonSchema,
});

type TeacherFormValues = z.infer<typeof teacherFormSchema>;

interface PcmpTeacherEditDialogProps {
  proposal: PcmpProposalDetail;
  onClose: () => void;
}

/** Legacy EditTeacherInfo: the teacher's profile, agreed and this proposal's amount. */
export function PcmpTeacherEditDialog({ proposal, onClose }: PcmpTeacherEditDialogProps) {
  const { teacher, payment } = proposal;
  const form = useForm<TeacherFormValues>({
    resolver: zodResolver(teacherFormSchema),
    defaultValues: {
      name: teacher.name,
      mobile: teacher.mobile ?? '',
      designation: teacher.designation ?? '',
      subject: teacher.subject ?? '',
      agreedAmount: String(teacher.agreedAmount),
      proposedAmount: String(payment.proposedAmount),
      comments: teacher.comments ?? '',
      reason: '',
    },
  });
  const { mutate, isPending, error } = useEditPcmpProposalSection(
    proposal.proposalId,
    'teacher',
    onClose,
  );

  const onSubmit = form.handleSubmit((values) =>
    mutate({
      name: values.name,
      mobile: values.mobile,
      designation: blankToNull(values.designation),
      subject: blankToNull(values.subject),
      agreedAmount: Number(values.agreedAmount),
      proposedAmount: Number(values.proposedAmount),
      comments: blankToNull(values.comments),
      reason: values.reason,
    }),
  );

  return (
    <Form {...form}>
      <PcmpEditDialog
        open
        onOpenChange={(open) => !open && onClose()}
        title="Edit Teacher"
        icon={UserPen}
        capabilities={proposal.capabilities}
        isDirty={form.formState.isDirty}
        isPending={isPending}
        error={error}
        onSubmit={(event) => void onSubmit(event)}
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  Name <span className="text-destructive">*</span>
                </FormLabel>
                <FormControl>
                  <Input {...field} autoComplete="off" maxLength={PCMP_EDIT_LIMITS.teacherName} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="mobile"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  Mobile <span className="text-destructive">*</span>
                </FormLabel>
                <FormControl>
                  <Input {...field} inputMode="tel" autoComplete="off" placeholder="01XXXXXXXXX" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="designation"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Designation</FormLabel>
                <FormControl>
                  <Input {...field} autoComplete="off" maxLength={PCMP_EDIT_LIMITS.shortText} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="subject"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Subject</FormLabel>
                <FormControl>
                  <Input {...field} autoComplete="off" maxLength={PCMP_EDIT_LIMITS.shortText} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="agreedAmount"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  Agreed amount <span className="text-destructive">*</span>
                </FormLabel>
                <FormControl>
                  <Input {...field} inputMode="decimal" autoComplete="off" />
                </FormControl>
                <FormDescription>The teacher&apos;s total for this submission.</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="proposedAmount"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  This proposal <span className="text-destructive">*</span>
                </FormLabel>
                <FormControl>
                  <Input {...field} inputMode="decimal" autoComplete="off" />
                </FormControl>
                <FormDescription>Amount proposed on {proposal.refNo}.</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <FormField
          control={form.control}
          name="comments"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Teacher note</FormLabel>
              <FormControl>
                <Textarea {...field} rows={2} maxLength={PCMP_EDIT_LIMITS.longText} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <PcmpEditReasonField control={form.control} />
      </PcmpEditDialog>
    </Form>
  );
}
